import { Link } from 'react-router-dom';

import { stopClickPropagation } from './stopClickPropagation';
import { useStageLinkData } from './useStageLinkData';

export interface PipelineStageValueLabelProps {
  /** Stage entity_id (UUID) or legacy stage_name (e.g. "ahoy_negotiation"). */
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /**
   * When true (default) and the stage's pipeline can be resolved, the label
   * renders as a link to the pipeline view with `?stage=<id>` — PipelineBoard
   * scrolls the matching column into view. Set false on surfaces where
   * navigating away is wrong (e.g. inside a modal flow like record merge).
   */
  interactive?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function humanizeStageName(value: string): string {
  const formatted = value.replace(/^ahoy_/, '').replace(/_/g, ' ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Display component for pipeline stage values.
 * Uses useStages hook to lookup stage by entity_id.
 *
 * When the stage's pipeline resolves (and `interactive` isn't false), the
 * label links to the pipeline view and scrolls the stage's column into view.
 *
 * @example
 * <PipelineStageValueLabel
 *   value="stage-entity-id-uuid"
 * />
 * // Renders: "Proposal" (linked to its pipeline)
 */
export const PipelineStageValueLabel: React.FC<
  PipelineStageValueLabelProps
> = ({ value, fontSize = 'sm', className = '', interactive = true }) => {
  // Non-UUID values are legacy stage_names (e.g. "ahoy_negotiation") that won't
  // resolve through the CRM object store. Skip the fetch and humanize directly
  // so users see "Negotiation" instead of a generic "Stage" fallback.
  const isUuid = typeof value === 'string' && UUID_REGEX.test(value);
  // Deep link to the stage's pipeline view. Null while unresolved (or for
  // legacy stage names), in which case the label stays a plain span.
  // `enabled: false` when the label can't or shouldn't navigate —
  // non-interactive, non-UUID, or no value — so opt-out labels never trigger
  // the pipelines fetch behind useStagePipelineRoute.
  const shouldResolveRoute = interactive && isUuid && Boolean(value);
  const { stage, isLoading, pipelineRoute } = useStageLinkData(
    isUuid ? (value ?? undefined) : undefined,
    { enabled: shouldResolveRoute }
  );
  const sizeClass = fontSizeClasses[fontSize];

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  if (!isUuid) {
    return (
      <span className={`${sizeClass} ${className}`}>
        {humanizeStageName(value)}
      </span>
    );
  }

  if (isLoading) {
    return <span className={`${sizeClass} ${className} animate-pulse`}>…</span>;
  }

  // Whenever we can't resolve a label — failed fetch, missing org context,
  // or a stage hydrated without ahoy_name — fall back to a safe generic
  // label. NEVER expose the raw entity_id (UUID) to users.
  if (!stage?.label) {
    return (
      <span className={`${sizeClass} ${className} text-fg-tertiary`}>
        Stage
      </span>
    );
  }

  if (pipelineRoute) {
    return (
      <Link
        to={pipelineRoute}
        title={`Open ${stage.label} in the pipeline`}
        onClick={stopClickPropagation}
        className={`${sizeClass} ${className} hover:underline hover:text-[var(--color-text-primary)] transition-colors`}
      >
        {stage.label}
      </Link>
    );
  }

  return <span className={`${sizeClass} ${className}`}>{stage.label}</span>;
};
